import { generateLeaseCompHeaderMapping } from "@/constants/leaseCompFields";
import { selectLeaseComps } from "@/features/survey/leaseCompsSelectors";
import {
  addLeaseComp,
  reorderLeaseComps as reorderLeaseCompsAction,
  setCurrentLeaseCompId,
  setLeaseComps,
  setOpenLeaseCompsDrawer,
} from "@/features/survey/leaseCompsSlice";
import { scrollToSection } from "@shared/utils";
import {
  useBulkImportLeaseCompsMutation,
  useCreateLeaseCompMutation,
  useImportLeaseCompsFromExcelMutation,
  useParseLeaseCompsMutation,
  useReorderLeaseCompsMutation,
} from "@/services/leaseComps";
import { useLeaseCompFieldRules } from "@/shared/hooks/useLeaseCompFieldRules";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldTooltip } from "@emerald-react/tooltip";
import { EmeraldButtonVariant } from "@emerald/nxcore";
import { EmptyState } from "@emerald-react/empty-state";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useSortableList } from "@hooks/useSortable";
import useSurveyState from "@hooks/useSurveyState";
import Analytics from "@shared/utils/tagManagerAnalytics";
import RowReportModal from "@survey-properties/RowReportModal";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import LeaseCompColumnMatchingModal from "./LeaseCompColumnMatchingModal";
import LeaseCompListItem from "./LeaseCompListItem";

// Extend LeaseComp with id field for sorting
interface LeaseCompSortable extends LeaseComp {
  id: string;
}

const LeaseCompsListDrawer = () => {
  const dispatch = useAppDispatch();
  const { survey } = useSurveyState();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedLeaseComps, setSelectedLeaseComps] = useState<Set<string>>(new Set());
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Upload flow state
  const [showColumnMatching, setShowColumnMatching] = useState(false);
  const [showRowReport, setShowRowReport] = useState(false);
  const [importResult, setImportResult] = useState<{
    matched: string[];
    notMatched: string[];
    unParsedColumns: string[][];
  } | null>(null);
  const [importSummary, setImportSummary] = useState<{
    toImport: number;
    notImported: number;
    totalProperties: number;
    geocodingErrors?: number;
  } | null>(null);
  const [parsedLeaseCompsData, setParsedLeaseCompsData] = useState<any>(null);
  const [showStylePinsTooltip, setShowStylePinsTooltip] = useState(false);

  // RTK Query hooks
  const [importLeaseCompsFromExcel, { isLoading: isUploading }] =
    useImportLeaseCompsFromExcelMutation();
  const [parseLeaseComps, parseResult] = useParseLeaseCompsMutation();
  const lastParsedRequestId = useRef<string | undefined>(undefined);
  // Tracks when we're programmatically transitioning from column matching → row report
  // so the EmeraldModal onClose callback doesn't clear importSummary mid-transition
  const isTransitioningToRowReport = useRef(false);
  const [bulkImportLeaseComps, { isLoading: isImporting }] =
    useBulkImportLeaseCompsMutation();
  const [createLeaseComp] = useCreateLeaseCompMutation();
  const [reorderLeaseComp] = useReorderLeaseCompsMutation();
  const { withLoading } = useSimpleConfirmation();
  const fieldRules = useLeaseCompFieldRules();

  const leaseComps = useAppSelector(selectLeaseComps);
  const isLeaseCompsDrawerOpen = useAppSelector((state) => state.leaseComps.isDrawerOpen);
  const isIncludeLeaseComps = survey?.isIncludeLeaseComps ?? false;
  const surveyId = survey?.id || "";

    // Reset checkbox selections when drawer state changes (opening or closing)
  useEffect(() => {
    setSelectedLeaseComps(new Set());
  }, [isLeaseCompsDrawerOpen]);

  // Sort lease comps by order and add id field for dnd-kit
  const leaseCompsWithId: LeaseCompSortable[] = [...leaseComps]
    .sort((a, b) => a.order - b.order)
    .map((comp) => ({ ...comp, id: comp.leaseCompId }));

  const handleReorder = (
    _reorderedComps: unknown[],
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    if (leaseComps.length === 0) {
      console.error("Cannot reorder: No lease comps available");
      return;
    }

    // Optimistic update
    dispatch(reorderLeaseCompsAction({ sourceIndex, destinationIndex }));

    reorderLeaseComp({
      surveyId,
      leaseCompId: leaseCompsWithId[sourceIndex]?.leaseCompId || "",
      order: destinationIndex,
    }).catch((_error) => {
      console.error("Failed to reorder lease comp");
      // Rollback: reorder back to original positions
      dispatch(
        reorderLeaseCompsAction({
          sourceIndex: destinationIndex,
          destinationIndex: sourceIndex,
        }),
      );
    });
  };

  const { DndContextProps, SortableContextProps } = useSortableList({
    items: leaseCompsWithId as any,
    onReorder: handleReorder,
  });

  const handleAddLeaseComp = async () => {
    let result;

    try {
      result = await createLeaseComp({
        surveyId,
        name: "New Lease Comp",
        isHidden: false,
        order: leaseCompsWithId.length,
        leaseCompFields: {},
      }).unwrap();

      // Optimistic update
      dispatch(addLeaseComp(result));
      dispatch(
        setCurrentLeaseCompId({ leaseCompId: result.leaseCompId || null }),
      );
    } catch (_error) {
      console.error("Failed to create lease comp");
    }

    if (!result) return;

    Analytics.send({
      event: "Added lease comp",
      SurveyID: survey?.id,
      leaseCompId: result.leaseCompId,
    });
  };

  // Derived checkbox states — always in sync with selectedLeaseComps
  const selectAllChecked = leaseCompsWithId.length > 0 && selectedLeaseComps.size === leaseCompsWithId.length;
  const isIndeterminate = selectedLeaseComps.size > 0 && selectedLeaseComps.size < leaseCompsWithId.length;

  // Sync indeterminate state on native checkbox (not settable via JSX prop)
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedLeaseComps(new Set(leaseCompsWithId.map(lc => lc.leaseCompId)));
    } else {
      setSelectedLeaseComps(new Set());
    }
  }, [leaseCompsWithId]);

  const handleToggleSelect = useCallback((id: string, selected: boolean) => {
    setSelectedLeaseComps(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const openMarketMapEditPanel = useCallback(() => {
    const marketMapSection = survey?.sections?.find((section) =>
      section.name?.toLowerCase().replace(/[_-]/g, " ").includes("market map"),
    );

    if (!marketMapSection) return;

    const openAccordion = (attempt = 0) => {
      // The sidebar accordion div has id={surveySectionId} set by BuilderAccordionSection
      const sidebarAccordion = document.getElementById(
        marketMapSection.surveySectionId,
      );

      if (!sidebarAccordion) {
        if (attempt < 5) {
          setTimeout(() => openAccordion(attempt + 1), 100);
        }
        return;
      }

      const isAccordionOpen =
        sidebarAccordion.querySelector(".accordion-header-open") !== null;

      if (!isAccordionOpen) {
        sidebarAccordion
          .querySelector<HTMLButtonElement>(".accordion-title")
          ?.click();
      }
    };

    setTimeout(() => scrollToSection(marketMapSection.surveySectionId), 100);
    setTimeout(() => openAccordion(), 200);
  }, [survey?.sections]);

  const handleStylePinsClick = () => {
    if (!isIncludeLeaseComps) {
      setShowStylePinsTooltip(true);
      return;
    }

    // Visible lease comps in list order (same filter as the list display)
    const visibleLeaseComps = leaseCompsWithId.filter((lc) => !lc.isHidden);

    // Build pin data using visible index (matches displayed pin numbers)
    const selectedPinsData = visibleLeaseComps
      .map((lc, visibleIdx) => ({ lc, number: visibleIdx + 1 }))
      .filter(({ lc }) => selectedLeaseComps.has(lc.leaseCompId))
      .map(({ lc, number }) => ({ number, id: lc.leaseCompId }));

    if (selectedPinsData.length === 0) return;

    // First visible selected pin — used to open the style drawer
    const firstSelectedId = selectedPinsData[0].id;

    // Store selected pin info for the style editor
    sessionStorage.setItem('selectedPinInfo', JSON.stringify({
      numbers: selectedPinsData.map(p => p.number),
      ids: selectedPinsData.map(p => p.id),
      type: 'lease comp'
    }));
    
    // Close the lease comps drawer
    dispatch(setOpenLeaseCompsDrawer(false));

    openMarketMapEditPanel();

    setTimeout(() => {
      const markerElement = document.getElementById(firstSelectedId);
      if (markerElement) {
        markerElement.click();
        setTimeout(() => {
          const styleButton = document.querySelector('#utility-style-property');
          if (styleButton && styleButton instanceof HTMLElement) {
            styleButton.click();
          }
        }, 200);
      }
    }, 300);
  };

  const handleUploadFromExcel = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".xls")
    ) {
      alert("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    // Reset all import state before starting a new import
    setImportResult(null);
    setImportSummary(null);
    setParsedLeaseCompsData(null);
    setShowColumnMatching(false);
    setShowRowReport(false);

    if (parseResult.reset) {
      parseResult.reset();
    }

    try {
      const result = await importLeaseCompsFromExcel({
        file,
        surveyId,
      }).unwrap();
      setImportResult(result);
      setShowColumnMatching(true);
    } catch (_error) {
      console.error("Failed to upload lease comps");
      alert("Failed to import Excel file. Please try again.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleColumnMatchingNext = (userMappings: Record<string, string>) => {
    if (!importResult) return;

    const defaultHeaderMapping = generateLeaseCompHeaderMapping();

    const userHeaderMapping = Object.entries(userMappings).map(
      ([header, matchedAttribute]) => ({ header, matchedAttribute }),
    );

    const userMappedHeaders = new Set(Object.keys(userMappings));
    const filteredDefaultMapping = defaultHeaderMapping.filter(
      (m) => !userMappedHeaders.has(m.header),
    );

    parseLeaseComps({
      unParsedColumns: importResult.unParsedColumns,
      headerMapping: [...userHeaderMapping, ...filteredDefaultMapping],
    });
  };

  // Handle parse result — keyed on requestId so it fires for every new call
  useEffect(() => {
    if (
      parseResult.isSuccess &&
      parseResult.data &&
      parseResult.requestId !== lastParsedRequestId.current
    ) {
      lastParsedRequestId.current = parseResult.requestId;
      const data = parseResult.data;

      // Handle various possible API response shapes
      const leaseCompsArray =
        data?.geocodedLeaseComps ??
        data?.leaseCompDocs ??
        data?.leaseComps ??
        (Array.isArray(data) ? data : []);

      const errors = data?.geocodingErrors ?? data?.errors ?? [];

      setParsedLeaseCompsData(data);
      setImportSummary({
        toImport: leaseCompsArray.length,
        notImported: errors.length,
        totalProperties: leaseCompsArray.length + errors.length,
        geocodingErrors: errors.length,
      });

      // Set transitioning flag so EmeraldModal's onClose callback
      // doesn't clear importSummary while we switch modals
      isTransitioningToRowReport.current = true;
      setShowColumnMatching(false);
      setTimeout(() => {
        isTransitioningToRowReport.current = false;
        setShowRowReport(true);
      }, 300);
    }
    if (
      parseResult.isError &&
      parseResult.requestId !== lastParsedRequestId.current
    ) {
      lastParsedRequestId.current = parseResult.requestId;
      setShowColumnMatching(false);
      alert("Failed to parse lease comps. Please try again.");
    }
  }, [
    parseResult.isSuccess,
    parseResult.isError,
    parseResult.data,
    parseResult.requestId,
  ]);

  const handleRowReportImport = async () => {
    if (!parsedLeaseCompsData) return;

    const leaseCompsArray =
      parsedLeaseCompsData?.geocodedLeaseComps ??
      parsedLeaseCompsData?.leaseCompDocs ??
      parsedLeaseCompsData?.leaseComps ??
      (Array.isArray(parsedLeaseCompsData) ? parsedLeaseCompsData : []);

    if (leaseCompsArray.length === 0) return;

    // Offset order by existing lease comps count so new ones are appended
    const existingCount = leaseComps.length;

    // Map flat parsed fields into LeaseCompRequest shape expected by backend
    const formattedLeaseComps = leaseCompsArray.map(
      (item: any, index: number) => ({
        name:
          item.propertyName ||
          item.name ||
          `Lease Comp ${existingCount + index + 1}`,
        isHidden: false,
        order: existingCount + index,
        source: item.source ?? null,
        leaseCompFields: item,
      }),
    );

    withLoading(
      async () =>
        await bulkImportLeaseComps({
          surveyId,
          leaseComps: formattedLeaseComps,
        }).unwrap(),
      { successText: "Lease comps successfully imported!" },
    )
      .then((result: any) => {
        if (result?.importedLeaseComps?.length) {
          dispatch(setLeaseComps(result.importedLeaseComps));
        }

      })
      .catch((_error: unknown) => {
        console.error("Failed to bulk import lease comps");
      });

    handleModalClose();
  };

  const handleModalClose = () => {
    setShowRowReport(false);
    setShowColumnMatching(false);
    setImportResult(null);
    setImportSummary(null);
    setParsedLeaseCompsData(null);
  };

  // Separate close handler for the column matching modal.
  // When EmeraldModal closes programmatically (opened → false) it fires its
  // onClose callback. During a modal transition we must NOT clear state.
  const handleColumnMatchingClose = () => {
    if (!isTransitioningToRowReport.current) {
      handleModalClose();
    }
  };

  return (
    <>
      <div className="h-full flex flex-col bg-white">
        {/* Header with action buttons */}
        <div className="px-4 pb-4 border-b border-slate-200">
          <div className="flex items-center justify-end gap-3">
            <EmeraldButton
              id="SC-CreateNewLeaseComp"
              icon="add"
              label="New Lease Comp"
              variant={EmeraldButtonVariant.TextButton}
              onClick={handleAddLeaseComp}
            />
            <EmeraldButton
              icon="cloud_upload"
              iconFillType="outlined"
              label={isUploading ? "Uploading..." : "Upload From Excel"}
              onClick={handleUploadFromExcel}
              disabled={isUploading}
              id="SC-UploadLeaseCompsFromExcel"
            />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
            />
          </div>
        </div>

        {/* Checkbox and Style Pins button */}
        {leaseCompsWithId.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
            {/* Left spacing to align with checkbox in list items (accounts for drag handle) */}
            <div style={{ width: '30px' }} />
                        {/* Native checkbox — derived state, never stale */}
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={selectAllChecked}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="cursor-pointer"
              style={{ width: '16px', height: '16px', accentColor: '#003f2d' }}
            />
            {!isIncludeLeaseComps ? (
              <EmeraldTooltip
                trigger="hover"
                placement="bottom"
                delayShow={0}
                delayHide={100}
                followCursor={false}
                interactive={true}
                closeOnOutsideClick={true}
                visible={showStylePinsTooltip}
                onVisibleChange={setShowStylePinsTooltip}
                triggerElement={
                  <span className="inline-flex cursor-not-allowed">
                    <EmeraldButton
                      label="Style Pins"
                      variant={EmeraldButtonVariant.Secondary}
                      onClick={handleStylePinsClick}
                      disabled={true}
                      style={{ pointerEvents: "none" }}
                    />
                  </span>
                }
              >
                <div className="max-w-xs text-xs leading-5">
                  To enable Style Pins for Lease Comps, please select{" "}
                  <a
                    href="#"
                    className="font-medium underline"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowStylePinsTooltip(false);
                      dispatch(setOpenLeaseCompsDrawer(false));
                      openMarketMapEditPanel();
                    }}
                  >
                    Show Comps
                  </a>{" "}
                  from the Market Map edit panel.
                </div>
              </EmeraldTooltip>
            ) : (
              <EmeraldButton
                label="Style Pins"
                variant={EmeraldButtonVariant.Secondary}
                onClick={handleStylePinsClick}
                disabled={selectedLeaseComps.size === 0}
              />
            )}
          </div>
        )}

        {/* List or empty state */}
        <div className="flex-1 overflow-y-auto">
          {leaseCompsWithId.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <EmptyState
                title={'No Lease Comps yet'}
                description={'Add your first lease comp to start comparing market data.'} 
                status={'no_data'}
                position="center"
              />
            </div>
          ) : (
            <DndContext {...DndContextProps}>
              <SortableContext {...SortableContextProps}>
                <div className="divide-y divide-divider-4">
                  {leaseCompsWithId.map((leaseComp, mapIndex) => {
                    const visibleCompsBeforeThis = leaseCompsWithId
                      .slice(0, mapIndex)
                      .filter((lc) => !lc.isHidden).length;

                    return (
                      <LeaseCompListItem
                        key={leaseComp.leaseCompId}
                        leaseComp={leaseComp}
                        index={visibleCompsBeforeThis}
                        isSelected={selectedLeaseComps.has(leaseComp.leaseCompId)}
                        onToggleSelect={handleToggleSelect}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <LeaseCompColumnMatchingModal
        isOpen={showColumnMatching}
        onClose={handleColumnMatchingClose}
        onNext={handleColumnMatchingNext}
        importResult={importResult}
        isProcessing={parseResult.isLoading}
        fieldRules={fieldRules}
      />

      <RowReportModal
        isOpen={showRowReport}
        onClose={handleModalClose}
        onImport={handleRowReportImport}
        importData={importSummary}
        isImporting={isImporting}
        entityName="Lease Comps"
      />
    </>
  );
};

export default LeaseCompsListDrawer;
